import React, { useState, useEffect } from "react";
import { useMutation } from "graphql-hooks";
import { DataGrid, gridClasses } from "@mui/x-data-grid";

import {
  Grid,
  Button,
  Divider,
  Typography,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

const useStyles = makeStyles(theme => ({
  outlinedButtonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#0D47A1",
    height: "34px",
    textTransform: "none",
  },
  buttonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#fff",
    height: "34px",
    backgroundColor: "#0D47A1",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#0D47A1",
    },
    "&:hover": {
      backgroundColor: "#1E88E5",
    },
  },
  dataGridStyle: {
    width: "100%",
    "& .super-app-theme--header": {
      backgroundColor: "#99D4FF",
      color: "#0D47A1",
      fontSize: 18,
      fontWeight: 700,
    },
    "&.MuiDataGrid-scrollbar MuiDataGrid-scrollbar--horizontal css-1rtad1": {
      position: "relative",
      display: "block",
    },
    "& .MuiDataGrid-scrollbarFiller": {
      //  backgroundColor: "#99D4FF"
    },
    "& .MuiDataGrid-cell": {
      fontSize: 16,
      fontWeight: 400,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      whiteSpace: "normal",
      lineHeight: "normal",
      textAlign: "center",
    },
    "& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    "& .MuiDataGrid-row.Mui-selected": {
      backgroundColor: "none !important",
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "none !important",
    },
    "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus":
      {
        outline: "none !important",
      },
    "&.MuiDataGrid-cell:focus-within": {
      outline: " none !important",
    },
    [`& .${gridClasses.row}.even`]: {
      backgroundColor: "#FFFFFF",
    },
    [`& .${gridClasses.row}.odd`]: {
      backgroundColor: "#E3F2FD",
    },
    [`& .${gridClasses.row}.green`]: {
      backgroundColor: "#EFFFCA",
    },
    [`& .${gridClasses.row}.even:hover`]: {
      backgroundColor: "#FFFFFF",
    },
    [`& .${gridClasses.row}.odd:hover`]: {
      backgroundColor: "#E3F2FD",
    },
    [`& .${gridClasses.row}.green:hover`]: {
      backgroundColor: "#EFFFCA",
    },
    [`& .${gridClasses.row}.even.Mui-selected`]: {
      backgroundColor: "#FFFFFF !important",
    },
    [`& .${gridClasses.row}.odd.Mui-selected`]: {
      backgroundColor: "#E3F2FD !important",
    },
    [`& .${gridClasses.row}.green.Mui-selected`]: {
      backgroundColor: "#EFFFCA !important",
    },
  },
}));

export default function TeamResourcesDialog({ selectedRow, resourcesList, closeDialog }) {
  const classes = useStyles();
  const [open, setOpen] = useState(true);

  const handleClose = () => {
    setOpen(false);
    closeDialog && closeDialog();
  };

  function CustomMembersNoRowsOverlay() {
    return <Box sx={{ textAlign: " Center", mt: 2 }}>No Members!</Box>;
  }

  function CustomStaffNoRowsOverlay() {
    return <Box sx={{ textAlign: " Center", mt: 2 }}>No Staff!</Box>;
  }

  const columns = [
    {
      field: "name",
      headerClassName: "super-app-theme--header",
      headerName: "Name",
      minWidth: 150,
      flex: 1,
      sortable: false,
      align: "left",
      headerAlign: "center",
      disableColumnMenu: true,
    },

    {
      field: "role",
      headerName: "Role",
      headerClassName: "super-app-theme--header",
      align: "center",
      headerAlign: "center",
      minWidth: 150,
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const membersRows = resourcesList
    .filter(item => item.userTypeId.typeName === "Member")
    .map((resource, index) => ({
      index,
      id: index,
      name: resource.userId.firstName + " " + resource.userId.lastName,
      role: resource.roleId.roleName,
    }));

  const staffRows = resourcesList
    .filter(item => item.userTypeId.typeName === "Staff")
    .map((resource, index) => ({
      index,
      id: index,
      name: resource.userId.firstName + " " + resource.userId.lastName,
      role: resource.roleId.roleName,
    }));

  return (
    <div style={{ width: "100%", minWidth: 500 }}>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              minWidth: "500px",
              maxHeight: "800px",
            },
          },
        }}
      >
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="alert-dialog-title"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Grid
            container
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Grid
              item
              xs={11}
              style={{
                textAlign: "center",
                paddingLeft: 15,
                fontSize: 18,
              }}
            >
              Team Term Resources
            </Grid>
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  align: "right",
                  color: theme => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h2"
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    paddingRight: 5,
                  }}
                >
                  Team:
                </Typography>
                {selectedRow && (
                  <Typography
                    variant="h3"
                    style={{
                      fontSize: 16,
                      fontWeight: 500,
                    }}
                  >
                    {selectedRow.teamName}
                  </Typography>
                )}
              </Box>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h2"
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    paddingRight: 5,
                  }}
                >
                  Term:
                </Typography>
                {selectedRow && (
                  <Typography
                    variant="h3"
                    style={{
                      fontSize: 16,
                      fontWeight: 500,
                    }}
                  >
                    {selectedRow.termName}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Box
              style={{
                textAlign: "center",
                paddingBottom: 20,
              }}
            >
              <Typography variant="h2">Members</Typography>
            </Box>
            <DataGrid
              className={classes.dataGridStyle}
              columns={columns}
              rows={membersRows}
              showCellRightBorder={true}
              hideFooter={true}
              autoWidth={true}
              slots={{
                noRowsOverlay: CustomMembersNoRowsOverlay,
              }}
              getRowId={row => row.id}
              getRowClassName={params => {
                if (params.row.index % 2 === 0) return "even";
                else return "odd";
              }}
              autoHeight={true}
            />
            <Box
              style={{
                textAlign: "center",
                paddingTop: 40,
                paddingBottom: 10,
              }}
            >
              <Typography variant="h2">Staff</Typography>
            </Box>
            <DataGrid
              className={classes.dataGridStyle}
              columns={columns}
              rows={staffRows}
              showCellRightBorder={true}
              hideFooter={true}
              autoWidth={true}
              slots={{
                noRowsOverlay: CustomStaffNoRowsOverlay,
              }}
              getRowId={row => row.id}
              getRowClassName={params => {
                if (params.row.index % 2 === 0) return "even";
                else return "odd";
              }}
              autoHeight={true}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Box mb={2}>
            <Button
              variant="contained"
              color="primary"
              className={classes.buttonStyle}
              onClick={handleClose}
            >
              Done
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// <Grid item xs={1}>
//   <Checkbox
//     //checked={item.checked}
//   //  onClick={(e) => handleCheckApplicant(e.target.checked, item)}
//   />
// </Grid>
